import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import LeaseEOIForm from '../../pages/Admin/LeaseEOIs/LeaseEOIForm';
import { useClickOutside } from '../../hooks/useClickOutside';
import type { LeaseEOI } from '../../types/lease-eoi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initialData?: LeaseEOI;
  onSubmit: (data: Omit<LeaseEOI, 'eoi_id'>) => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
  title: string;
  submitLabel: string;
  preventCloseOnOutsideClick?: boolean;
}

export default function LeaseEOIFormModal({
  isOpen,
  onClose,
  initialData,
  onSubmit,
  isSubmitting,
  error,
  title,
  submitLabel,
  preventCloseOnOutsideClick = true
}: Props) {
  const modalContentRef = useRef<HTMLDivElement>(null);
  useClickOutside(modalContentRef, onClose, preventCloseOnOutsideClick);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start md:items-center justify-center p-0 z-[100] overflow-y-auto"
    >
      <div 
        ref={modalContentRef}
        className="relative bg-white w-full md:max-w-2xl md:rounded-lg md:my-8 min-h-screen md:min-h-0 max-h-full md:max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex justify-between items-center p-4 md:p-6 border-b bg-white">
          <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
          <button 
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content - with scrollable area */}
        <div className="p-4 md:p-6 overflow-y-auto flex-1">
          <LeaseEOIForm
            initialData={initialData}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            error={error}
            onCancel={onClose}
            submitLabel={submitLabel}
            formId="lease-eoi-form"
          />
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex justify-end gap-3 p-4 md:p-6 border-t bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="lease-eoi-form"
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-md disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
